<template>
  <div
    v-infinite-scroll="loadMore"
    class="content-tracking"
    infinite-scroll-disabled="busy"
    infinite-scroll-distance="15"
    infinite-scroll-immediate-check="false"
  >
    <div class="fixed-content">
      <div class="bgcolor px-3 pt-3">
        <b-row class="px-1">
          <b-col class="d-flex flex-lg-row flex-sm-column justify-content-between">
            <div class="d-flex align-items-center mb-sm-2 mb-lg-0">
              <div
                v-if="sectionsActivated && selectedOption && !selectedOption.elementWithoutOperation"
                v-testing="'default-switch'"
                class="switch-btn p-0 d-flex align-items-center mr-3"
              >
                <input
                  id="toggle"
                  v-model="isDefaultOption"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggle"
                  class="switch mb-0"
                />
              </div>

              <searcher-module
                v-if="sectionsActivated"
                ref="searcherModule"
                name="laboratorySearcher"
                class="mx-3"
                :error-message="$tc('laboratory.error.validIdentifierTracking')"
                clear-input-after-search
                subportion-search
                portion-search
                @subportionSearch="onSubportionSearch($event)"
                @portionSearch="onPortionSearch($event)"
              />

              <template v-if="sectionsActivated && data.length">
                <i
                  class="mr-2 ml-4 fas fa-caret-right main"
                  aria-hidden="true"
                />
                <span class="main mr-2">{{ sectionTitle }}</span>
                <span class="dark font-size--small-3">{{ selectedRows.length }}/{{ selectableRowsCount }} {{ $tc('laboratory.tracking.contentSelected') }}</span>
              </template>
            </div>

            <div
              v-if="sectionsActivated && data.length"
              class="d-flex align-items-center"
            >
              <div
                v-if="canMarkSelectedOptionAsDepletedOrNonDepleted"
                class="d-flex"
              >
                <div class="p-0 d-flex align-items-center mr-2">
                  <input
                    id="toggle-cut"
                    v-model="internalDepleted"
                    type="checkbox"
                    class="offscreen"
                    aria-hidden="true"
                    @change="toggleDepleted"
                  >
                  <label
                    for="toggle-cut"
                    class="switch mb-0"
                  />
                </div>
                <span class="mr-4 font-size--small-3">{{ $t('laboratory.tracking.depletedBlock') }}</span>
              </div>
              <vt-loading-button
                v-if="selectedOption && !selectedOption.elementWithoutOperation"
                id="exportLaboratoryGridsToPdf"
                v-testing="'export'"
                class="tr-btn main ml-3"
                :params="{ promise: exportPdf }"
              >
                <i
                  class="fal fa-print"
                  aria-hidden="true"
                />
              </vt-loading-button>
              <!-- TODO: Cancellation front development required -->
              <b-button
                v-if="false"
                class="tr-btn main ml-3 d-flex align-items-center justify-content-center"
                qa="content-tracking-cancellation-button"
                @click="openCancellationModalForm()"
              >
                <i
                  class="far fa-times"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-testing="'instrument'"
                :disabled="!canAddDevice"
                class="tr-btn main ml-3 d-flex align-items-center justify-content-center"
                @click="openDevicesModalForm()"
              >
                <i
                  class="icon-instrument"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                :disabled="!selectedRows.length"
                class="tr-btn main ml-3 d-flex align-items-center justify-content-center"
                qa="content-tracking-manual-shipment-button"
                @click="showManualShipmentModal"
              >
                <i
                  class="far fa-box"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="$_canPrintSubportion() && type==='subportion'"
                v-testing="'print-subportions'"
                :disabled="!selectedRows.length"
                class="tr-btn main ml-3"
                @click="printSubportionsLabels(selectedRows)"
              >
                <i
                  class="fal fa-ticket-alt"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="$_canCreateIncident()"
                v-testing="'incidents'"
                :disabled="selectedRows.length !== 1"
                class="tr-btn error ml-3"
                @click="showIncidentFormModal"
              >
                <i
                  class="fal fa-exclamation-triangle"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="canUndoLastOperationOfSelectedSidebarOption"
                v-testing="'undo-action'"
                :disabled="!canUndoLastOperationOfSelectedElements"
                class="tr-btn ml-3"
                style="background-color: #fc6274"
                @click="showUndoActionConfirmationModal"
              >
                <i
                  class="fal fa-arrow-left"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="canProcessElementsOfSelectedOption && selectedOption && selectedOption.id !== 'operation.entity.portion.cut'"
                v-testing="'apply-action'"
                :disabled="!selectedRows.length"
                class="tr-btn go ml-3"
                @click="applyAction(selectedOption.actionUrl)"
              >
                <i
                  class="fal fa-arrow-right"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row
          v-if="selectedOption && selectedOption.elementWithoutOperation"
          class="px-1"
        >
          <b-col>
            <div class="alert-card d-flex align-items-center py-2 mt-2 mb-0">
              <div>
                <i
                  class="fas fa-exclamation-triangle"
                  aria-hidden="true"
                />
              </div>
              <div style="line-height: 14px">
                <h3>{{ $t('laboratory.tracking.elementWithoutProcess') }}</h3>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-row>
        <div class="gradation" />
      </b-row>
    </div>

    <div v-if="sectionsActivated && data.length">
      <b-row
        v-for="(elementData, index) in data"
        :key="index"
        class="cards-column"
      >
        <b-col>
          <div @click="selectElement(index)">
            <single-card-tracking
              ref="singleCardTracking"
              :lab-id="$store.getters.labId"
              :type="type"
              :information="elementData"
              :user-profile-image-cancel-token="sourceProfileImage.token"
              @showMoreInfoModal="showMoreInfoModal"
              @isDepleted="updateDepleted"
              @updateList="onSingleCardTrackingUpdateList"
              @updateContent="getData"
              @updateCount="updateCount"
              @openIncidentsModal="openIncidentsModal"
              @openAttachmentsListModal="openAttachmentsListModal"
              @select="onSingleCardTrackingSelect"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <div v-if="!sectionsActivated">
      <div class="d-flex align-items-center flex-column justify-content-center laboratory-empty-state">
        <b-container>
          <div class="d-flex align-items-center flex-column">
            <div class="d-flex align-items-center justify-content-center picture">
              <img
                src="@/img/tracking-empty-state.png"
                alt="empty"
              >
              <div class="circle" />
            </div>
            <h3 class="dark mt-4">
              {{ $t('laboratory.emptyState.setupSections') }}
            </h3>
          </div>
          <b-row class="w-100 px-5 mt-5 d-flex justify-content-around">
            <b-col
              cols="3"
              class="d-flex align-items-center justify-content-center"
            >
              <div class="d-flex">
                <i
                  class="main fal fa-cog fa-2x mx-3"
                  aria-hidden="true"
                />
                <span>{{ $t('laboratory.emptyState.setupIcon') }}</span>
              </div>
            </b-col>
            <b-col
              cols="3"
              class="d-flex align-items-center justify-content-center"
            >
              <div class="d-flex">
                <i
                  class="main fal fa-check-square fa-2x mx-3"
                  aria-hidden="true"
                />
                <span>{{ $t('laboratory.emptyState.selectSections') }}</span>
              </div>
            </b-col>
            <b-col
              cols="3"
              class="d-flex align-items-center justify-content-center"
            >
              <div class="d-flex">
                <i
                  class="main fal fa-list-ul fa-2x mx-3"
                  aria-hidden="true"
                />
                <span>{{ $t('laboratory.emptyState.viewList') }}</span>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-5 text-center">
            <b-col
              cols="12"
              class="mt-5"
            >
              <span
                class="main"
                style="font-size: 15px"
              >{{ $t('global.emptyState.needHelp') }}</span>
            </b-col>
            <b-col cols="12">
              <b-button class="btn__icon btn__icon--primary main">
                {{ $t('global.emptyState.userManual') }}
              </b-button>
              <b-button class="btn__icon btn__icon--primary main">
                {{ $t('global.emptyState.technicalSupport') }}
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </div>
    <div v-if="!data.length && sectionsActivated && !busy && !showMessageAlt">
      <b-row class="p-5">
        <b-col>
          <h3 class="primary-dark-color">
            <i
              class="mr-3 far fa-empty-set"
              aria-hidden="true"
            />{{ $t('laboratory.tracking.noPendingElements') }}
          </h3>
          <h5 class="mt-3 label-dark">
            {{ $t('laboratory.tracking.selectAnotherProcess') }}
          </h5>
          <i
            class="mt-4 main arrow-animation fal fa-arrow-alt-left"
            aria-hidden="true"
          />
        </b-col>
      </b-row>
    </div>
    <div v-if="!data.length && sectionsActivated && !busy && showMessageAlt">
      <b-row class="p-5">
        <b-col>
          <h3 class="primary-dark-color">
            {{ $t('laboratory.emptyState.selectSections') }}
          </h3>
          <h5 class="mt-3">
            {{ $t('laboratory.tracking.pendingProcesses') }}
          </h5>
          <i
            class="mt-4 main arrow-animation fal fa-arrow-alt-left"
            aria-hidden="true"
          />
        </b-col>
      </b-row>
    </div>
    <div v-if="busy">
      <b-row class="px-4 mt-3">
        <b-col cols="12">
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .6;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .5;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .4;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .3;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .2;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
          <b-card
            class="py-2 mb-3"
            style="border-radius: 1rem; opacity: .1;"
          >
            <div class="d-flex justify-content-between">
              <div class="d-flex">
                <div>
                  <div style="width: 35px;height:35px;border:1px solid #d8d8d8; border-radius:50%;" />
                </div>
                <div class="mx-3">
                  <div style="width: 200px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 120px; height: 10px;background: #d8d8d8" />
                </div>
                <div class="mx-3">
                  <div style="width: 100px; height: 20px;background: #d8d8d8" />
                  <div style="margin-top: 2px;width: 60px; height: 10px;background: #d8d8d8" />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div
                  class="mx-2"
                  style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;"
                />
                <div style="width: 20px; height:20px;border-radius:50%;background:#d8d8d8;" />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <b-row v-if="busy">
      <b-col class="text-center">
        <b-spinner
          variant="primary"
          label="Text Centered"
        />
      </b-col>
    </b-row>
    <print-component ref="printComponent" />
    <modal-confirmation
      id="laboratoryTrackingConfirmationModal"
      ref="laboratoryConfirmationModal"
      :content="$t('laboratory.content.otherProcessConfirmation')"
    />
    <modal-incident-form
      ref="incidentFormModal"
      @incidentAdded="getData"
      @incidentClosed="afterIncidentClosed"
    />
    <modal-incident-list
      ref="modalIncidentList"
      @openIncidentFormModal="$refs.incidentFormModal.show($event.coreEntity, $event.entitiesIds, $event.incidentId, $event.elementSelected, $event.incidentLabel)"
    />
    <modal-more-info ref="moreInfoModal" />
    <laboratory-tracking-instrument-modal-form
      ref="laboratoryTrackingInstrumentModalForm"
      :title="$tc('global.instrument')"
      :options="selectedRows"
      :devices="instrumentOptions"
      :fixed-device="instrumentSelected"
      @device-applied="onDeviceApplied"
    />
    <laboratory-tracking-cancellation-modal-form
      ref="laboratoryTrackingCancellationModalForm"
      :elements-number="selectedRows.length"
    />
    <attachments-list-modal-detail ref="attachmentsListModalDetail" />
    <manual-shipment
      ref="manualShipmentContentTracking"
      :samples="selectedSamplesForManualShipment"
      :portions="selectedPortionsForManualShipment"
      :subportions="selectedSubportionsForManualShipment"
      @manualShipmentApplied="onManualShipmentApplied"
    />
  </div>
</template>

<script>
import Encoder from '@/utils/encoder'
import laboratorySidebarStore from '@/modules/laboratoryModule/sideBar/laboratorySideBarStore'
import SearcherModule from '@/components/SearcherModule'
import SingleCardTracking from '@/modules/laboratoryModule/laboratoryTracking/SingleCardTracking'
import LaboratoryDynamicServices from '@/modules/laboratoryModule/services/laboratoryDynamicServices'
import { isEqual } from 'lodash'
import sectionsMixin from '@/modules/laboratoryModule/mixins/sectionsMixin'
import instrumentsMixin from '@/modules/laboratoryModule/mixins/instrumentsMixin'
import portionCardFields from './portionCardFieldsForReporting'
import subportionCardFields from './subportionCardFieldsForReporting'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import LABORATORY_OPERATIONS from '@/modules/laboratoryModule/config/operations'
import ModalIncidentForm from '@/modules/laboratoryModule/laboratoryTracking/modals/ModalIncidentForm'
import ModalIncidentList from '@/modules/laboratoryModule/laboratoryTracking/modals/ModalIncidentList'
import AttachmentsListModalDetail from '@/modules/laboratoryModule/laboratoryTracking/modals/attachments-list-modal-detail/attachments-list-modal-detail.vue'
import LaboratoryTrackingInstrumentModalForm from '@/modules/laboratoryModule/laboratoryTracking/modals/LaboratoryTrackingInstrumentModalForm'
import LaboratoryTrackingCancellationModalForm from '@/modules/laboratoryModule/laboratoryTracking/modals/laboratory-tracking-cancellation-modal-form/laboratory-tracking-cancellation-modal-form.vue'
import ModalMoreInfo from '@/modules/laboratoryModule/laboratoryTracking/modals/ModalMoreInfo'
import { ENTITIES_LABORATORY } from '@/modules/laboratoryModule/config/sidebarConfig'
import LaboratoryOptionGuardsMixin from '@/modules/laboratoryModule/mixins/LaboratoryOptionGuardsMixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { IDENTIFIER_SCOPES } from '@/api/resources/identifierScopesResource'
import axios from 'axios'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'
import ManualShipment from '@/components/manual-shipment/manual-shipment.vue'
import apiExceptionType from '@/utils/apiExceptionType'
import { commonPortionService } from '@/api/services/common/portion/common-portion.service'

export default {
    name: 'ContentTracking',
    components: {
        SearcherModule,
        SingleCardTracking,
        ModalConfirmation,
        ModalIncidentForm,
        ModalMoreInfo,
        ModalIncidentList,
        LaboratoryTrackingInstrumentModalForm,
        LaboratoryTrackingCancellationModalForm,
        AttachmentsListModalDetail,
        ManualShipment
    },
    mixins: [
        LaboratoryOptionGuardsMixin,
        IncidentOperationGuardMixin,
        sectionsMixin,
        instrumentsMixin
    ],
    props: {
        sectionTitle: String,
        showMessageAlt: Boolean
    },
    data() {
        return {
            searchActive: false,
            disableDataLoad: false,
            encoder: new Encoder(),
            sectionsActivated: 0,
            data: [],
            internalDepleted: false,
            selectedRows: [],
            selectableRowsCount: 0,
            responseInformation: null,
            element: null,
            busy: false,
            LABORATORY_OPERATIONS: LABORATORY_OPERATIONS,
            updateTaskCountInterval: null,
            sourceProfileImage: axios.CancelToken.source()
        }
    },
    computed: {
        selectedSamplesForManualShipment() {
            let samples = []
            if (this.selectedOption?.type === ENTITIES_LABORATORY.SAMPLE && !this.busy) {
                samples = this.selectedRows.length ? this.selectedRows.map(selectedRow => ({
                    caseId: selectedRow.caseStudy.casId,
                    sampleId: selectedRow.id,
                    label: selectedRow.sampleLabel
                })) : []
            }
            return samples
        },
        selectedPortionsForManualShipment() {
            let portions = []
            if ((this.selectedOption?.type === ENTITIES_LABORATORY.PORTION ||
                this.selectedOption?.typeAlt === ENTITIES_LABORATORY.PORTION) &&
                !this.busy) {
                portions = this.selectedRows.length ? this.selectedRows.map(selectedRow => ({
                    caseId: selectedRow.sample.caseStudy.casId,
                    sampleId: selectedRow.sample.samId,
                    portionId: selectedRow.porId,
                    label: selectedRow.porLabel
                })) : []
            }
            return portions
        },
        selectedSubportionsForManualShipment() {
            let subportions = []
            if (this.selectedOption?.type === ENTITIES_LABORATORY.SUBPORTION &&
                !this.selectedOption.typeAlt && !this.busy) {
                subportions = this.selectedRows.length ? this.selectedRows.map(selectedRow => ({
                    caseId: selectedRow.portion.sample.caseStudy.casId,
                    sampleId: selectedRow.portion.sample.samId,
                    portionId: selectedRow.portion.porId,
                    subportionId: selectedRow.supId,
                    label: selectedRow.supLabel
                })) : []
            }
            return subportions
        },
        canMarkSelectedOptionAsDepletedOrNonDepleted() {
            return this.selectedRows.length === 1 &&
                (this.selectedOption?.id === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id ||
                this.selectedOption?.id === LABORATORY_OPERATIONS.PARAFFINBLOCK_ARCHIVE.id)
        },
        isDepleted() {
            return this.canMarkSelectedOptionAsDepletedOrNonDepleted && this.selectedRows[0].depleted
        },
        isDefaultOption: {
            get: () => {
                if (laboratorySidebarStore.state.selectedOption) {
                    return laboratorySidebarStore.state.config.defaultOptionId === laboratorySidebarStore.state.selectedOption.id
                } else {
                    return false
                }
            },
            set: () => {
                if (laboratorySidebarStore.state.config.defaultOptionId === laboratorySidebarStore.state.selectedOption.id) {
                    laboratorySidebarStore.setDefaultOption(null)
                } else {
                    laboratorySidebarStore.setDefaultOption(laboratorySidebarStore.state.selectedOption)
                }
                laboratorySidebarStore.saveConfig()
            }
        },
        regex() {
            return this.$store.getters.regex
        },
        type() {
            if (this.selectedOption?.operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id) {
                return this.selectedOption.typeAlt
            }
            return this.selectedOption?.type
        },
        url() {
            if (this.selectedOption?.operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id) {
                return this.selectedOption.urlAlt
            }
            return this.selectedOption?.url
        },
        canUndoLastOperationOfSelectedElements() {
            return !!this.selectedRows.length && !!this.data.length &&
                this.data.every(element => element.checked ? element.lastOperationCanBeUndone : true)
        },
        canAddDevice() {
            return !this.selectedOption?.elementWithoutOperation && !!this.selectedRows.length && !!this.instrumentOptions.length
        }
    },
    watch: {
        async selectedOption() {
            await this.loadData()
        }
    },
    async mounted() {
        if (this.$route.params.sidebarOption) {
            const option = laboratorySidebarStore.findOptionById(this.$route.params.sidebarOption)
            laboratorySidebarStore.setSelectedOption(option)
        }

        this.$eventHub.$on('trackingSectionsUpdated', this.onTrackingSectionUpdated)

        this.updateTaskCountInterval = this.$_setSummaryInterval(() => this.$eventHub.$emit('updateTaskCount'))
    },
    beforeDestroy() {
        this.$eventHub.$off('trackingSectionsUpdated', this.onTrackingSectionUpdated)
        if (this.updateTaskCountInterval !== null) {
            clearInterval(this.updateTaskCountInterval)
        }
    },
    methods: {
        async onTrackingSectionUpdated(sections) {
            this.sectionsActivated = sections.filter(section => section.checked).length
            this.data = []

            if (this.selectedOption) {
                await this.loadData()
            }
        },
        onSingleCardTrackingSelect(information) {
            this.internalDepleted = information.depleted
        },
        onManualShipmentApplied() {
            this.loadData()
        },
        showManualShipmentModal() {
            this.$refs.manualShipmentContentTracking.show()
        },
        async loadData() {
            if (this.disableDataLoad) {
                this.disableDataLoad = false
            } else {
                if (this.selectedOption) {
                    await this.getData(true)
                }
            }

            this.resetInstrumentsData()
            this.$nextTick(() => this.$refs.searcherModule?.focusInput())
        },
        openCancellationModalForm() {
            this.$refs.laboratoryTrackingCancellationModalForm.show()
        },
        openDevicesModalForm() {
            this.$refs.laboratoryTrackingInstrumentModalForm.show()
        },
        async showUndoActionConfirmationModal() {
            const confirmation = await this.showConfirmationModalGlobal(this.$t('laboratory.content.askUndoActionConfirmation'))
            if (confirmation) {
                await this.applyAction(this.getUndoUrlOfSelectedOption(), this.type)
            }
        },
        async afterIncidentClosed() {
            if (this.$refs.modalIncidentList?.entitiesIds) {
                await this.$refs.modalIncidentList.loadIncidents()
                this.$refs.modalIncidentList.showModal()
            }
        },
        openIncidentsModal(event) {
            const type = this.selectedOption.typeAlt || this.selectedOption.type
            this.$refs.modalIncidentList.show(type.toUpperCase(), event.entitiesIds, event.information)
        },
        openAttachmentsListModal(event) {
            this.$refs.attachmentsListModalDetail.show(event, this.type)
        },
        async printSubportionsLabels(selectedSubportions) {
            for (const subportion of selectedSubportions) {
                await this.$refs.printComponent.printSubportionsLabels(
                    this.$store.getters.labId,
                    subportion.portion.sample.caseStudy.casId,
                    subportion.portion.sample.samId,
                    subportion.portion.porId,
                    [subportion])
            }
            await this.getData()
        },
        showMoreInfoModal(information) {
            this.$refs.moreInfoModal.show(this.type === ENTITIES_LABORATORY.PORTION)
            const isSelectedOptionParaffinBlockCut = this.selectedOption.id === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id
            if (this.type === ENTITIES_LABORATORY.PORTION || isSelectedOptionParaffinBlockCut) {
                this.$refs.moreInfoModal.loadPortionInformation(information)
            } else if (this.type === ENTITIES_LABORATORY.SUBPORTION) {
                this.$refs.moreInfoModal.loadSubportionInformation(information)
            }
        },
        showIncidentFormModal() {
            const type = this.selectedOption.typeAlt || this.selectedOption.type
            const elementChecked = this.selectedRows[0]
            let entitiesIds = []
            if (type === ENTITIES_LABORATORY.PORTION) {
                entitiesIds = [
                    elementChecked.sample.caseStudy.casId,
                    elementChecked.sample.samId,
                    elementChecked.porId
                ]
            } else {
                entitiesIds = [
                    elementChecked.portion.sample.caseStudy.casId,
                    elementChecked.portion.sample.samId,
                    elementChecked.portion.porId,
                    elementChecked.supId
                ]
            }
            this.$refs['incidentFormModal'].show(type.toUpperCase(), entitiesIds, null, elementChecked)
        },

        async _innerProcessSearch(operation, element) {
            this.searchActive = true

            if (operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id) {
                this.data = []
            }

            const elementIsNotAdded =
                (this.selectedOption.type === ENTITIES_LABORATORY.PORTION &&
                    this.data.findIndex(item => item.porId === element.porId) < 0) ||
                (this.selectedOption.type === ENTITIES_LABORATORY.SUBPORTION &&
                    this.data.findIndex(item => item.supId === element.supId) < 0) ||
                (this.selectedOption.operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id &&
                    this.data.findIndex(item => item.porId === element.porId) < 0)
            if (elementIsNotAdded) {
                element.checked = true
                this.data.push(element)
                await this.selectElement()
            } else {
                this.$_showWarningModal(this.$tc('laboratory.tracking.elementAlreadyAdded'))
            }

            if (operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id) {
                this.$nextTick(() => this.$refs.singleCardTracking[0].showCutModal())
            }
        },
        async processSearch(operation, element) {
            if (operation === LABORATORY_OPERATIONS.PARAFFINBLOCK_SUBPORTION_CUT.id) {
                this.$_showErrorModal(this.$tc('laboratory.tracking.subportionCutTrackingSearch'))
                return
            }

            if (this.selectedOption?.id === operation) {
                if (!this.searchActive) {
                    this.data = []
                }

                await this._innerProcessSearch(operation, element)
            } else {
                this.$refs.laboratoryConfirmationModal?.ask(async () => {
                    this.disableDataLoad = true
                    const optionToSelect =
                        this.state.customOptions.find(option => option.id === operation || option.idAlt === operation)
                    laboratorySidebarStore.setSelectedOption(optionToSelect)

                    this.data = []
                    await this._innerProcessSearch(operation, element)
                })
            }
        },
        async onSubportionSearch(searchInformation) {
            LaboratoryDynamicServices.cancelAxiosCall()
            const response =
                await this.$_toCallUnique(
                    LaboratoryDynamicServices.getSubportionsSearchByLabel(
                        this.$store.getters.labId,
                        searchInformation.search,
                        this.sectionIds),
                    error => {
                        if (!axios.isCancel(error)) {
                            this.$_showErrorModal(
                                error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                                    ? this.$tc('laboratory.content.elementNotFound')
                                    : undefined)
                        }
                    })

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IDENTIFIER_SCOPES.SUBPORTION,
                            entityValue: entity.subportion,
                            laboratoryId: entity.subportion.portion.sample.caseStudy.laboratory?.labId
                        }))
                    const entityValueSelected =
                        await this.$_showMultiEntitySelection(
                            searchInformation.search,
                            foundEntities)

                    entitySelected = response.find(entity => entity.subportion.id === entityValueSelected.id)
                }

                const operation = entitySelected.laboratoryOperations[0]
                const subportion = entitySelected.subportion
                if (operation) {
                    await this.processSearch(operation, subportion)
                } else {
                    this.disableDataLoad = true
                    laboratorySidebarStore.setSelectedOption({
                        type: ENTITIES_LABORATORY.SUBPORTION,
                        elementWithoutOperation: true
                    })

                    this.data = [subportion]
                    await this.selectElement()
                }
            }
        },
        async onPortionSearch(searchInformation) {
            LaboratoryDynamicServices.cancelAxiosCall()
            const response =
                await this.$_toCallUnique(
                    LaboratoryDynamicServices.getPortionsSearchByLabel(
                        this.$store.getters.labId,
                        searchInformation.search,
                        this.sectionIds),
                    error => {
                        if (!axios.isCancel(error)) {
                            this.$_showErrorModal(
                                error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                                    ? this.$tc('laboratory.content.elementNotFound')
                                    : undefined)
                        }
                    })

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IDENTIFIER_SCOPES.PORTION,
                            entityValue: entity.portion,
                            laboratoryId: entity.portion.sample.caseStudy.laboratory?.labId
                        }))
                    const entityValueSelected =
                        await this.$_showMultiEntitySelection(
                            searchInformation.search,
                            foundEntities)

                    entitySelected = response.find(entity => entity.portion.id === entityValueSelected.id)
                }

                const operation = entitySelected.laboratoryOperations[0]
                const portion = entitySelected.portion
                if (operation) {
                    await this.processSearch(operation, portion)
                } else {
                    this.disableDataLoad = true
                    laboratorySidebarStore.setSelectedOption({
                        type: ENTITIES_LABORATORY.PORTION,
                        elementWithoutOperation: true
                    })

                    this.data = [portion]
                    await this.selectElement()
                }
            }
        },

        async loadMore() {
            if (this.responseInformation && !this.responseInformation.last) {
                const params = {
                    size: 20,
                    page: this.responseInformation.pageable.pageNumber + 1
                }
                await this.getData(true, params)
            }
        },
        async updateCount() {
            await this.selectElement()
            this.$eventHub.$emit('updateTaskCount')
        },
        async selectElement() {
            this.selectableRowsCount = this.data.length
            this.selectedRows = this.data.filter(elem => elem.checked)

            if (this.selectedOption.id !== LABORATORY_OPERATIONS.PARAFFINBLOCK_CUT.id) {
                await this.loadInstrumentOptions(this.selectedRows)
            }
        },
        _keepSelectedRows() {
            const selectedIds = this.selectedRows.map(elem => {
                if (this.selectedOption.type === ENTITIES_LABORATORY.PORTION || this.selectedOption.typeAlt === ENTITIES_LABORATORY.PORTION) {
                    return this.getIdHierarchyFromPortion(elem)
                } else if (this.selectedOption.type === ENTITIES_LABORATORY.SUBPORTION) {
                    return this.getIdHierarchyFromSubportion(elem)
                }
            })

            this.data.forEach(elem => {
                let elemId
                const type = this.selectedOption.typeAlt || this.selectedOption.type
                if (type === ENTITIES_LABORATORY.PORTION) {
                    elemId = this.getIdHierarchyFromPortion(elem)
                } else if (type === ENTITIES_LABORATORY.SUBPORTION) {
                    elemId = this.getIdHierarchyFromSubportion(elem)
                }
                if (selectedIds.find(selectedId => isEqual(selectedId, elemId))) {
                    elem.checked = true
                }
            })
            this.selectedRows = this.data.filter(elem => elem.checked)
        },
        _cancelProfileImageCalls() {
            this.sourceProfileImage.cancel()
            this.sourceProfileImage = axios.CancelToken.source()
        },
        async onSingleCardTrackingUpdateList() {
            await this.updateCount()
            await this.getData()
        },
        async getData(deselectAll = false, params) {
            this.searchActive = false
            if (!params) {
                this.data = []
                window.scrollTo(0, 0)
            }

            if (this.sectionIds.length) {
                this.busy = true
                try {
                    this._cancelProfileImageCalls()
                    LaboratoryDynamicServices.cancelAxiosCall()

                    const response =
                        await this.$_toCallSecure(
                            LaboratoryDynamicServices.paginator(
                                undefined,
                                { ...params, sectionIds: this.sectionIds },
                                this.$store.getters.labId,
                                this.url))
                    if (response) {
                        this.responseInformation = response
                        this.data = this.data.concat(response.content)
                        this.selectableRowsCount = response.totalElements
                    }

                    if (this.data.length) {
                        if (deselectAll) {
                            this.selectedRows = []
                        } else {
                            this._keepSelectedRows()
                        }
                    }
                } finally {
                    this.busy = false
                }
            }
        },
        async applyAction(url, elementType = this.selectedOption.type) {
            let params
            if (this.selectedRows.length) {
                params = this.selectedRows.map(elem => {
                    if (elementType === ENTITIES_LABORATORY.PORTION) {
                        return this.getIdHierarchyFromPortion(elem)
                    } else if (elementType === ENTITIES_LABORATORY.SUBPORTION) {
                        return this.getIdHierarchyFromSubportion(elem)
                    }
                })
                let [err] = await this.toCall(LaboratoryDynamicServices.applyAction(this.$store.getters.labId, this.selectedOption.sectionId, url, params))
                if (err) {
                    this.showErrorModalGlobal(this.$t('laboratory.content.errorAction'))
                    return
                }
                this.showSuccessModalGlobal(this.$t('laboratory.content.actionOk'))
                this.$eventHub.$emit('updateTaskCount')
                await this.getData(true)
            }
        },
        async exportPdf() {
            const type = this.selectedOption.typeAlt || this.selectedOption.type
            const laboratoryIds =
                this.selectedRows?.map(selectedRow => {
                    if (type === ENTITIES_LABORATORY.PORTION) {
                        return selectedRow.porId
                    } else if (this.selectedOption.type === ENTITIES_LABORATORY.SUBPORTION) {
                        return selectedRow.supId
                    }
                }) ?? []
            const visibleColumns =
                type === ENTITIES_LABORATORY.PORTION
                    ? portionCardFields
                    : subportionCardFields

            const blob =
                await this.$_toCallUnique(
                    LaboratoryDynamicServices.exportTo(
                        this.$store.getters.labId,
                        this.state.selectedOption.label,
                        undefined,
                        undefined,
                        visibleColumns,
                        laboratoryIds,
                        this.url,
                        this.sectionIds,
                        CommonExportType.PDF),
                    () => this.$_showErrorModal(this.$tc('print.modals.errorReport')))
            if (blob) {
                this.$_openFile({ blob })
            }
        },
        async updateDepleted() {
            if (this.internalDepleted) {
                await this.markPortionAsDepleted(this.selectedRows[0])
                this.$emit('reload-sidebar-summary')
            } else {
                await this.markPortionAsNonDepleted(this.selectedRows[0])
            }
            await this.getData(true)
        },
        async toggleDepleted() {
            await this.updateDepleted()
        },
        async markPortionAsDepleted(portion) {
            const entityIds = [{
                caseId: portion.sample.caseStudy.casId,
                portionId: portion.porId,
                sampleId: portion.sample.samId
            }]
            const laboratoryId = this.$store.getters.labId
            const [err] = await this.toCall(commonPortionService.markPortionsAsDepleted(laboratoryId, entityIds, false))
            if (err) {
                if (err.response?.data?.code === apiExceptionType.MARK_AS_DEPLETED_PORTION_HAS_SUBPORTIONS_PENDING_CUT) {
                    const shouldCancelSubportionsPendingCut = await this.showConfirmationModalGlobal(err.response.data.message)
                    if (shouldCancelSubportionsPendingCut) {
                        const [err2] = await this.toCall(commonPortionService.markPortionsAsDepleted(laboratoryId, entityIds, true))
                        if (err2) {
                            this.showErrorModalGlobal()
                            this.internalDepleted = false
                        } else {
                            this.showToastSuccessGlobal(this.$tc('laboratory.content.blockMarkAsDepletedSuccessfully'))
                            this.internalDepleted = true
                        }
                    } else {
                        this.internalDepleted = false
                    }
                } else {
                    this.showErrorModalGlobal()
                    this.internalDepleted = false
                }
            } else {
                this.showToastSuccessGlobal(this.$tc('laboratory.content.blockMarkAsDepletedSuccessfully'))
                this.internalDepleted = true
            }
        },
        async markPortionAsNonDepleted(params) {
            const element = {
                caseId: params.sample.caseStudy.casId,
                portionId: params.porId,
                sampleId: params.sample.samId
            }
            const [err] = await this.toCall(commonPortionService.markPortionsAsNonDepleted(this.$store.getters.labId, [element]))
            if (err) {
                this.showErrorModalGlobal()
                this.internalDepleted = true
            }
            this.showToastSuccessGlobal(this.$t('laboratory.content.blockMarkAsNonDepletedSuccessfully'))
            this.internalDepleted = false
        },
        async onDeviceApplied(deviceFixed) {
            this.instrumentSelected = deviceFixed

            await this.getData()
        }
    }
}

</script>

<style lang="scss">
    .content-tracking .alert-card {
        border: 1px solid vt-color(grid-general-border);
        border-radius: 3px;
        border-left: 3px solid vt-color(warning);
        background: vt-color(warning, .8);
    }

    .content-tracking .alert-card i {
        color: vt-color(warning);
        font-size: font-size(very-big);
        margin: 0 10px;
    }

    .content-tracking .alert-card h3 {
        margin-bottom: 0;
        font-size: font-size(normal);
        font-weight: bold;
    }

    .content-tracking .alert-card span {
        font-size: font-size(small);
    }

    .content-tracking .instrument-selected {
        background: vt-color(primary) !important;
        color: vt-color(white-light) !important;
    }

    .content-tracking .laboratory-empty-state{
        background: transparent;
        margin-top: 3rem;
    }

    .content-tracking .laboratory-empty-state .picture{
        position:relative;
        height: 150px;
        width: 150px;
    }

    .content-tracking .laboratory-empty-state .picture img{
        position:relative;
        z-index: 1;
        width: 150px;
    }

    .content-tracking .laboratory-empty-state .picture .circle {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: vt-color(primary);
        opacity: .2;
        border-radius: 50%;
        z-index: 0;
    }

    .content-tracking .spinner > div {
        background-color: vt-color(primary);
    }

    .content-tracking .spinner .diamond {
        border-radius: 50%;
        background: vt-color(primary);
    }

    .content-tracking .search-component .touch-screen {
        display: block;
    }

    .content-tracking .search-component .touch-screen .search-button {
        width: 40px;
    }

    .content-tracking .search-component .desktop-screen {
        display: none;
    }

    .content-tracking {
        .vt-search-bar {
            input {
                max-width: none;
                min-width: 0;
                display: table;
            }
        }
    }

    .tr-btn.disabled {
        background: vt-color(base-text, .5);
    }
    .tr-btn.disabled i {
        color: vt-color(white-light) !important;
    }
    .tr-btn.main i {
        color: vt-color(primary) !important;
    }
    .tr-btn.error i {
        color: #fc6274 !important;
    }

</style>

<style scoped lang="scss">

.switch-btn .switch {
  position: relative;
  display: inline-block;
  width: 45px;
  height: 25px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
  font-family: "Font Awesome 5 Pro",sans-serif;
}

.switch-btn .switch::after {
  content: "\F08D";
  position: absolute;
  width: 23px;
  height: 23px;
  padding: 2px 6px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}

.switch {
  position: relative;
  display: inline-block;
  width: 45px;
  height: 25px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  transition: all 0.3s;
  font-family: "fontello",sans-serif;
}

.switch::after {
  content: "\e813";
  position: absolute;
  width: 23px;
  height: 23px;
  padding: 2px 5px;
  border-radius: 18px;
  background-color: white;
  top: 1px;
  left: 1px;
  transition: all 0.3s;
}

input[type="checkbox"]:checked + .switch::after {
  transform: translateX(20px);
}

input[type="checkbox"]:checked + .switch {
  background-color: vt-color(primary);
}

input[type="checkbox"]:checked + .switch::after {
  color:vt-color(primary);
}

.offscreen {
  position: absolute;
  left: -9999px;
}

    .arrow-animation {
        font-size: 50px;
        animation: shake 2s cubic-bezier(.36, .07, .19, .97) both;
        animation-iteration-count: infinite;
    }

    @keyframes shake {
        10%, 90% {
            transform: translate3d(-1px, 0, 0);
        }

        20%, 80% {
            transform: translate3d(2px, 0, 0);
        }

        30%, 50%, 70% {
            transform: translate3d(-4px, 0, 0);
        }

        40%, 60% {
            transform: translate3d(4px, 0, 0);
        }
    }

    .gradation {
        background: rgb(253, 187, 45);
        background: linear-gradient(0deg, rgba(253, 187, 45, 0) 0%, rgba(250, 250, 250, 1) 100%);
        height: 15px;
        width: 100%;
    }

    .content-tracking .fixed-content {
        position: sticky;
        top: 0;
        /* background:#fafafa; */
        z-index: 2;
    }

    .content-tracking .fixed-content .bgcolor {
        background: #fafafa;
    }

    .content-tracking .fixed-content small {
        font-size: 12px;
    }

    .content-tracking .cards-column {
        margin: 0 15px;
    }

    .tr-btn {
        border-radius: 50%;
        width: 40px;
        height: 40px;
        background: #FFF;
        border-color: #d8d8d8;
    }
    .tr-btn.disabled {
        background: vt-color(base-text, .5);
    }
    .tr-btn.disabled i {
        color: vt-color(white-light) !important;
    }

    .tr-btn.main:hover {
        color: vt-color(primary);
    }

    .tr-btn.error:hover {
        color: vt-color(red-medium);
    }

    .tr-btn.go {
        background:vt-color(green-radiant);
    }

    .tr-btn.neutral {
        color: vt-color(light-grey);
    }

</style>
